"use client";

import { useRouter } from "next/navigation";
import { useState, type SubmitEvent } from "react";

type LoginFormProps = {
  locale: string;
  title: string;
  intro?: string | undefined;
};

// Render the real login form and call the auth route handler.
export function LoginForm({ locale, title, intro }: LoginFormProps) {
  const router = useRouter();
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Submit credentials and redirect when authentication succeeds.
  async function handleSubmit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    setIsSubmitting(true);
    setMessage(null);

    try {
      const response = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ login, password }),
      });

      const payload = (await response.json().catch(() => null)) as
        | { ok?: boolean; message?: string; code?: string; mustChangePassword?: boolean }
        | null;

      if (payload?.code === "PASSWORD_RESET_REQUIRED") {
        router.push(`/${locale}/login/redefinir-senha`);
        router.refresh();
        return;
      }

      if (!response.ok || !payload?.ok) {
        setMessage(payload?.message ?? "Não foi possível realizar o login.");
        return;
      }

      if (payload.mustChangePassword) {
        router.push(`/${locale}/login/redefinir-senha`);
        router.refresh();
        return;
      }

      router.push(`/${locale}/restrito`);
      router.refresh();
    } catch {
      setMessage("Erro inesperado ao tentar autenticar. Tente novamente.");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <section className="rounded-3xl border border-blue-900/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.97),rgba(235,243,252,0.99))] p-6 shadow-[0_18px_45px_rgba(15,23,42,0.08)] sm:p-8">
      <p className="text-sm font-semibold uppercase tracking-[0.22em] text-blue-700">Sistech Sistemas</p>
      <h1 className="mt-3 text-3xl font-semibold tracking-tight text-blue-950 sm:text-4xl">{title}</h1>
      {intro ? <p className="mt-4 max-w-3xl text-base leading-7 text-slate-700 sm:text-lg">{intro}</p> : null}

      <form className="mt-8 space-y-5" onSubmit={handleSubmit} noValidate>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="login" className="text-sm font-medium text-blue-950">
              Login
            </label>
            <input
              id="login"
              name="login"
              type="text"
              className="mt-2 w-full rounded-2xl border border-blue-900/15 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-600 focus:ring-2 focus:ring-blue-300/60"
              value={login}
              onChange={(event) => setLogin(event.target.value)}
              autoComplete="username"
              required
            />
          </div>

          <div>
            <label htmlFor="password" className="text-sm font-medium text-blue-950">
              Senha
            </label>
            <input
              id="password"
              name="password"
              type="password"
              className="mt-2 w-full rounded-2xl border border-blue-900/15 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-600 focus:ring-2 focus:ring-blue-300/60"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
              required
            />
          </div>
        </div>

        <p className="rounded-2xl border border-blue-900/10 bg-[linear-gradient(180deg,rgba(239,246,255,0.92),rgba(255,255,255,0.98))] px-4 py-3 text-sm text-slate-700">
          Acesso restrito à área de clientes e back-office.
        </p>

        <p className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Primeiro acesso detectado? Você será redirecionado para definir sua senha antes de entrar na área restrita.
        </p>

        <div className="mt-2 text-sm">
          <a href={`/${locale}/login/esqueci-senha`} className="text-blue-600 hover:underline">
            Esqueci a senha
          </a>
        </div>

        {message ? (
          <p className="rounded-2xl border border-blue-900/10 bg-white px-4 py-3 text-sm text-blue-900" role="status" aria-live="polite">
            {message}
          </p>
        ) : null}

        <button
          type="submit"
          className="inline-flex rounded-full bg-[linear-gradient(135deg,#12356d,#2563eb)] px-5 py-3 text-sm font-semibold text-white shadow-md shadow-blue-950/15 transition-colors hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-75"
          disabled={isSubmitting}
        >
          {isSubmitting ? "Entrando..." : "Entrar"}
        </button>
      </form>
    </section>
  );
}
